import React, { useState, useEffect } from 'react';
import { useNavigate, Outlet } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import {
  Box,
  Button,
  Card,
  Container,
  Grid,
  Stack,
  Typography,
  TextField,
  MenuItem,
  IconButton,
  Tooltip,
  TablePagination,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Avatar,
  CircularProgress,
} from '@mui/material';
import { Add, Edit, Delete, Visibility, FilterList } from '@mui/icons-material';
// Page component replaced with Box
import { EMPLOYEE_TYPES, EMPLOYEE_STATUSES } from '../../types/employee.types';
import { useEmployees } from '../../context';

// Import the Employee type from the types file
import { Employee } from '../../types/employee.types';

// This is needed to properly type the employees data from the context
interface EmployeesState {
  employees: Employee[];
  loading: boolean;
  error: string | null;
}

const EmployeesPage: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { state, fetchEmployees, deleteEmployee } = useEmployees();
  
  // Type assertion for the employees state
  const employeesState = state as unknown as EmployeesState;
  const employees: Employee[] = Array.isArray(employeesState?.employees) ? employeesState.employees : [];
  const loading = employeesState?.loading || false;
  
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [employeeTypeFilter, setEmployeeTypeFilter] = useState<string>('');
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [page, setPage] = useState(0);

  const filteredEmployees = employees.filter((employee: Employee) => {
    const matchesSearch = searchTerm
      ? (employee.user?.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
         employee.user?.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
         employee.user?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
         employee.employeeId?.toLowerCase().includes(searchTerm.toLowerCase()))
      : true;
    const matchesStatus = statusFilter
      ? employee.status === statusFilter
      : true;
    const matchesEmployeeType = employeeTypeFilter
      ? employee.employeeType === employeeTypeFilter
      : true;
    return matchesSearch && matchesStatus && matchesEmployeeType;
  });

  const paginatedEmployees = filteredEmployees.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  ) as Employee[];

  useEffect(() => {
    const fetchData = async () => {
      try {
        await fetchEmployees({
          page: page + 1, // page is zero-based for TablePagination, API expects 1-based
          limit: rowsPerPage,
          search: searchTerm,
          status: statusFilter,
          employeeType: employeeTypeFilter
        });
      } catch (error) {
        enqueueSnackbar('Failed to fetch employees', { variant: 'error' });
      }
    };
    fetchData();
  }, [page, rowsPerPage, searchTerm, statusFilter, employeeTypeFilter, enqueueSnackbar, fetchEmployees]);

  // Handle loading state
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  const handleAddEmployee = () => {
    navigate('/settings/employees/new');
  };

  const handleViewEmployee = (id: string) => {
    navigate(`/settings/employees/${id}`);
  };

  const handleRowClick = (id: string) => {
    handleViewEmployee(id);
  };

  const handleEditEmployee = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`/settings/employees/${id}/edit`);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this employee?')) {
      try {
        await deleteEmployee(id);
        enqueueSnackbar('Employee deleted successfully', { variant: 'success' });
      } catch (error) {
        enqueueSnackbar('Failed to delete employee', { variant: 'error' });
      }
    }
  };

  const handleStatusFilterChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    setStatusFilter(event.target.value as string);
  };

  const handleEmployeeTypeFilterChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    setEmployeeTypeFilter(event.target.value as string);
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return 'success';
      case 'INACTIVE':
        return 'default';
      case 'ON_LEAVE':
        return 'warning';
      case 'TERMINATED':
        return 'error';
      default:
        return 'default';
    }
  };

  // If we have an outlet, it means we're rendering a nested route
  if (window.location.pathname !== '/settings/employees') {
    return <Outlet />;
  }

  return (
    <Box sx={{ py: 4 }}>
      <Container maxWidth="xl">
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
          <Typography variant="h4" gutterBottom>
            Employees
          </Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleAddEmployee}
          >
            New Employee
          </Button>
        </Stack>

        <Card sx={{ p: 3, mb: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Search employees"
                variant="outlined"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by name, email, or ID..."
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                select
                fullWidth
                label="Status"
                value={statusFilter}
                onChange={handleStatusFilterChange}
                variant="outlined"
              >
                <MenuItem value="">All Statuses</MenuItem>
                {EMPLOYEE_STATUSES.map((status) => (
                  <MenuItem key={status.value} value={status.value}>
                    {status.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                select
                fullWidth
                label="Employee Type"
                value={employeeTypeFilter}
                onChange={handleEmployeeTypeFilterChange}
                variant="outlined"
              >
                <MenuItem value="">All Types</MenuItem>
                {EMPLOYEE_TYPES.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={4} sx={{ display: 'flex', alignItems: 'center' }}>
              <Button
                variant="outlined"
                startIcon={<FilterList />}
                onClick={() => {
                  setSearchTerm('');
                  setStatusFilter('');
                  setEmployeeTypeFilter('');
                }}
                sx={{ ml: 'auto' }}
              >
                Clear Filters
              </Button>
            </Grid>
          </Grid>
        </Card>

        <Card>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Employee</TableCell>
                  <TableCell>ID</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Department</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Joined Date</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {state.loading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : state.employees.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                      No employees found
                    </TableCell>
                  </TableRow>
                ) : (
                  state.employees.map((employee) => (
                    <TableRow
                      key={employee.id}
                      hover
                      onClick={() => handleViewEmployee(employee.id)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Avatar 
                            sx={{ 
                              width: 40, 
                              height: 40, 
                              mr: 2, 
                              bgcolor: employee?.userId ? 'primary.main' : 'grey.500' 
                            }}
                          >
                            {employee?.firstName?.[0] || 'E'}
                            {employee?.lastName?.[0] || ''}
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle2">
                              {employee?.firstName || 'Employee'} {employee?.lastName || ''}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {employee?.email || `ID: ${employee.employeeId}`}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>{employee.employeeId}</TableCell>
                      <TableCell>
                        {EMPLOYEE_TYPES.find((t) => t.value === employee.employeeType)?.label || employee.employeeType}
                      </TableCell>
                      <TableCell>{employee.department?.name || 'N/A'}</TableCell>
                      <TableCell>
                        <Chip
                          label={employee.status.replace('_', ' ')}
                          color={getStatusColor(employee.status) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {employee.joiningDate
                          ? new Date(employee.joiningDate).toLocaleDateString()
                          : 'N/A'}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="View">
                          <IconButton
                            onClick={(e: React.MouseEvent) => {
                              e.stopPropagation();
                              handleViewEmployee(employee.id);
                            }}
                          >
                            <Visibility fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton
                            onClick={(e: React.MouseEvent) => {
                              e.stopPropagation();
                              handleEditEmployee(employee.id, e);
                            }}
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            onClick={(e: React.MouseEvent) => {
                              e.stopPropagation();
                              handleDelete(employee.id);
                            }}
                            color="error"
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={state.total}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Card>
      </Container>
    </Box>
  );
};

export default EmployeesPage;
